import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';

import ContentContainer from '@rebound/design-system/components/ContentContainer';
import { ThemeColorScheme } from '@rebound/shared-models/retailer/retailer.model';

import PageLayout from '@components/PageLayout';
import ListWrapper from '@modules/dashboard/components/ListWrapper';
import OrderPlaceholder from '@modules/dashboard/components/OrderPlaceholder';
import ReturnCard from '@modules/dashboard/components/ReturnCard';
import { useRetailerData } from '@modules/retailer/retailer.context';

import HeroSection from '../components/HeroSection';
import {
  FindReturnByTrackingQuery,
  FindReturnByTrackingQueryResult,
} from '../tracking.queries';

const DEFAULT_LANGUAGE = 'en';

const TrackReturnPage = () => {
  const retailerData = useRetailerData();
  const router = useRouter();
  const [findReturnByTracking, { data: trackReturnListData, loading }] =
    useLazyQuery<FindReturnByTrackingQueryResult>(FindReturnByTrackingQuery, {
      fetchPolicy: 'no-cache',
      onError: (e) => {
        toast.error(e.message);
      },
    });

  const changeLanguage = (lang: string) => {
    i18next.changeLanguage(lang);
  };

  useEffect(() => {
    const lang = router.query.lang as string;
    if (lang) {
      changeLanguage(lang);
    } else {
      changeLanguage(DEFAULT_LANGUAGE);
    }
  }, [router.query.lang]);

  return (
    <>
      <NextSeo title="Track Return - Rebound Returns" />
      <PageLayout
        logoSrc={retailerData.retailer?.theme.logo || ''}
        faviconSrc={retailerData.retailer?.theme.favicon || ''}
        isRetailerDataLoading={retailerData.loading}
        advertisingBanner={retailerData.retailer?.advertisingBanner}
      >
        <HeroSection
          findReturnByTracking={findReturnByTracking}
          trackReturnData={trackReturnListData?.findReturnByTracking}
          scheme={
            retailerData.retailer?.theme.colorScheme ?? ThemeColorScheme.Neutral
          }
        />

        {trackReturnListData?.findReturnByTracking &&
          trackReturnListData?.findReturnByTracking.map(
            (returnItem) =>
              returnItem && (
                <ListWrapper
                  key={returnItem.id}
                  data-cy="matched-return-card-item"
                >
                  <ContentContainer>
                    <ReturnCard
                      returnObject={returnItem}
                      scheme={retailerData.retailer?.theme.colorScheme}
                      trackReturnPreview
                    />
                  </ContentContainer>
                </ListWrapper>
              ),
          )}
        {loading && (
          <ListWrapper>
            <ContentContainer>
              <OrderPlaceholder />
            </ContentContainer>
          </ListWrapper>
        )}
      </PageLayout>
    </>
  );
};

export default TrackReturnPage;
